import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

type ActionMode = "resetPassword" | "verifyEmail";

type Props = {
  defaultMode?: ActionMode;
};

const ACTION_BASE_URL = "https://mimocupido.firebaseapp.com/__/auth/action";

const ActionScreen: React.FC<Props> = ({ defaultMode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [openError, setOpenError] = useState<string | null>(null);

  const { mode, targetUrl, errorMessage } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawMode = params.get("mode");
    const resolvedMode =
      rawMode === "resetPassword" || rawMode === "verifyEmail"
        ? (rawMode as ActionMode)
        : defaultMode;

    if (!resolvedMode) {
      return {
        mode: undefined,
        targetUrl: null,
        errorMessage:
          "Link inválido ou incompleto. Abra o link do e-mail novamente.",
      } as const;
    }

    params.set("mode", resolvedMode);

    const oobCode = params.get("oobCode");
    const apiKey = params.get("apiKey");

    if (!oobCode || !apiKey) {
      return {
        mode: resolvedMode,
        targetUrl: null,
        errorMessage:
          "Não foi possível continuar porque faltam parâmetros do link (oobCode/apiKey).",
      } as const;
    }

    const url = `${ACTION_BASE_URL}?${params.toString()}`;

    return {
      mode: resolvedMode,
      targetUrl: url,
      errorMessage: null,
    } as const;
  }, [defaultMode, location.search]);

  const title = mode === "verifyEmail" ? "Verificar e-mail" : "Redefinir senha";
  const description =
    mode === "verifyEmail"
      ? "Para confirmar seu e-mail, clique no botão abaixo."
      : "Para redefinir sua senha, clique no botão abaixo.";

  const buttonLabel =
    mode === "verifyEmail" ? "Verificar e-mail" : "Redefinir senha";

  const onRedirect = () => {
    if (!targetUrl) return;
    setIsRedirecting(true);
    setOpenError(null);

    const opened = window.open(targetUrl, "_blank", "noopener,noreferrer");
    if (!opened) {
      setIsRedirecting(false);
      setOpenError(
        "Seu navegador bloqueou a abertura de nova aba. Permita pop-ups para este site e tente novamente.",
      );
    }
  };

  const goLogin = () => {
    navigate("/login");
  };

  return (
    <section className="stats">
      <div className="container">
        <div className="stat-card cta-content center auth-card">
          <h1 className="auth-title">{title}</h1>

          {errorMessage ? (
            <p className="auth-subtitle">{errorMessage}</p>
          ) : (
            <p className="auth-subtitle">{description}</p>
          )}

          {openError ? <div className="form-error">{openError}</div> : null}

          <div className="auth-actions">
            <button
              className="btn-primary"
              type="button"
              disabled={!targetUrl || isRedirecting}
              onClick={onRedirect}
            >
              {isRedirecting ? "Abrindo em nova aba..." : buttonLabel}
            </button>

            <button className="btn-primary" type="button" onClick={goLogin}>
              Fazer Login
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ActionScreen;
